import "./TeamLeaveRequests.scss";
import {
  Button,
  DatePicker,
  Empty,
  Grid,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  fixedTableScrollY,
  formatEnumLabel,
  getRequestStatusType,
  Notify,
  RequestStatus,
  rowsPerPage,
} from "../../utils/constants";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dayjs } from "dayjs";
import { LeaveRequest } from "../../utils/types";
import { useUser } from "../../context/UserContext";
import RequestStatusModal from "../Modals/RequestStatusModal";
import MonthlyExportModal from "../Modals/MonthlyExportModal";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type {
  FilterDropdownProps,
  SortOrder,
} from "antd/es/table/interface";
import { apiClient } from "../../utils/httpClient";
import { canReviewAsManager, formatRequestedAt } from "../../utils/helpers";
import { ViewDocumentsBtn } from "../Buttons/ViewDocumentsBtn";
import { RequestIdLink } from "../Buttons/RequestIdLink";
import {
  activeColumn,
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../Filters/EmployeeStatusFilter";

const SORT_DIRECTIONS: SortOrder[] = ["ascend", "descend"];

const TeamLeaveRequests = () => {
  const { authUser, roles } = useUser();
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [currentViewData, setCurrentViewData] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(
    null,
  );
  const [selectedRequestStatus, setSelectedRequestStatus] =
    useState<RequestStatus>(RequestStatus.APPROVE);
  const [isRequestStatusOpen, setIsRequestStatusOpen] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(rowsPerPage);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [requestDateRange, setRequestDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [isMonthlyExportOpen, setIsMonthlyExportOpen] =
    useState<boolean>(false);
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);

  const normalizedRoles = useMemo(
    () => roles.map((role) => role.replace(/^ROLE_/, "")),
    [roles],
  );
  const isAdmin = normalizedRoles.includes("ADMIN");
  const isHR = normalizedRoles.includes("HR");
  const isManager = normalizedRoles.includes("MANAGER");
  const canViewAllRequests = isAdmin || isHR;
  const canApproveRequests = isAdmin || isManager;
  const currentUserEmail = authUser?.email;
  // HR (+Manager) loads every request via getAllLeaveRequests, so the role
  // alone isn't enough - only offer actions on requests this user actually
  // manages (or is the OoO substitute for).
  const canReviewRequest = useCallback(
    (leaveRequest: LeaveRequest) =>
      canApproveRequests &&
      canReviewAsManager(leaveRequest, currentUserEmail, isAdmin),
    [canApproveRequests, currentUserEmail, isAdmin],
  );
  const canExportRequests = isAdmin || isHR;

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const requestSeq = useRef(0);
  const { RangePicker } = DatePicker;

  const fetchLeaveRequests = useCallback(async () => {
    let api = "";
    let currentRequestSeq = 0;

    if (canViewAllRequests) {
      api = `${process.env.REACT_APP_API_URL}/getAllLeaveRequests`;
    } else if (isManager) {
      api = `${process.env.REACT_APP_API_URL}/getRequestsByTeam`;
    } else {
      return;
    }

    try {
      currentRequestSeq = ++requestSeq.current;
      setIsLoading(true);
      const response = await apiClient.get(api);

      if (currentRequestSeq !== requestSeq.current) {
        return;
      }

      setLeaveRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch leave requests data:", error);
      Notify(
        "error",
        "Could not load leave requests data. Please try again.",
      );
    } finally {
      if (currentRequestSeq === requestSeq.current) {
        setIsLoading(false);
      }
    }
  }, [canViewAllRequests, isManager]);

  useEffect(() => {
    if (!authUser) return;
    fetchLeaveRequests();
  }, [authUser, fetchLeaveRequests]);

  useEffect(() => {
    if (!isRequestStatusOpen && authUser) {
      fetchLeaveRequests();
    }
  }, [authUser, fetchLeaveRequests, isRequestStatusOpen]);

  const dateFilteredLeaveRequests = useMemo(() => {
    const statusRank = (status?: string) => {
      if (status === "AWAITING_MANAGER") return 0;
      if (status === "APPROVED") return 1;
      if (status === "REJECTED") return 2;
      return 3;
    };
    const sortedData = leaveRequests
      .filter((request) =>
        matchesEmployeeStatus(request.employeeStatus, employeeStatusFilter),
      )
      .sort((a, b) => {
        const byStatus = statusRank(a.requestStatus) - statusRank(b.requestStatus);
        if (byStatus !== 0) return byStatus;
        return (b.requestCreationDate || "").localeCompare(a.requestCreationDate || "");
      });

    if (!requestDateRange?.[0] || !requestDateRange?.[1]) {
      return sortedData;
    }

    const filterStart = requestDateRange[0].startOf("day").toDate();
    const filterEnd = requestDateRange[1].endOf("day").toDate();
    return sortedData.filter((request) => {
      if (!request.startDate || !request.endDate) return false;
      const requestStart = new Date(request.startDate);
      const requestEnd = new Date(request.endDate);
      return requestStart <= filterEnd && requestEnd >= filterStart;
    });
  }, [leaveRequests, requestDateRange, employeeStatusFilter]);

  useEffect(() => {
    setCurrentViewData(dateFilteredLeaveRequests);
  }, [dateFilteredLeaveRequests]);

  useEffect(() => {
    setCurrentPage(1);
  }, [employeeStatusFilter]);

  const requestTypeFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          leaveRequests
            .map((request) => request.requestType)
            .filter((requestType) => !!requestType),
        ),
      ).map((requestType) => ({
        text: formatEnumLabel(requestType),
        value: requestType,
      })),
    [leaveRequests],
  );

  const requestStatusFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          leaveRequests
            .map((request) => request.requestStatus)
            .filter((requestStatus) => !!requestStatus),
        ),
      ).map((requestStatus) => ({
        text: formatEnumLabel(requestStatus),
        value: requestStatus,
      })),
    [leaveRequests],
  );

  const getTextColumnFilterProps = useCallback(
    (
      dataIndex: keyof Pick<
        LeaveRequest,
        "employeeName" | "substituteName" | "comment" | "approverName"
      >,
      placeholder: string,
    ): ColumnType<LeaveRequest> => ({
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(event) => event.stopPropagation()}>
          <Input
            placeholder={placeholder}
            value={(selectedKeys[0] as string) || ""}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        String(record[dataIndex] || "")
          .toLowerCase()
          .includes(String(value).toLowerCase()),
    }),
    [],
  );

  const handleReportDownload = useCallback(async () => {
    if (!canExportRequests) return;

    if (currentViewData.length === 0) {
      Notify("info", "No leave requests to export.");
      return;
    }

    try {
      setIsExporting(true);
      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/exportLeaveRequests/filtered`,
        {
          leaveRequestIds: currentViewData.map((request) => request.id),
        },
        { responseType: "blob" },
      );

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] || "application/vnd.ms-excel",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const contentDisposition = response.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(
        /filename="?([^";\n]+)"?/,
      );
      const filename = filenameMatch
        ? filenameMatch[1]
        : "leave_requests_filtered.xls";

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Failed to export leave requests:", error);
      Notify("error", "Could not export leave requests.");
    } finally {
      setIsExporting(false);
    }
  }, [canExportRequests, currentViewData]);

  const tableColumns: ColumnsType<LeaveRequest> = useMemo(
    () => [
      {
        title: "Actions",
        key: "actions",
        fixed: !isMobile ? "left" : undefined,
        width: 140,
        render: (_: unknown, leaveRequest: LeaveRequest) => (
          <div className="flex flex-row gap-2">
            {leaveRequest.requestStatus === "AWAITING_MANAGER" &&
              canReviewRequest(leaveRequest) && (
                <>
                  <Tooltip title="Approve request">
                    <Button
                      type="text"
                      className="status-action-btn"
                      icon={<CheckOutlined />}
                      onClick={() => {
                        setSelectedRequest(leaveRequest);
                        setSelectedRequestStatus(RequestStatus.APPROVE);
                        setIsRequestStatusOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Reject request">
                    <Button
                      type="text"
                      className="status-action-btn"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setSelectedRequest(leaveRequest);
                        setSelectedRequestStatus(RequestStatus.REJECT);
                        setIsRequestStatusOpen(true);
                      }}
                    />
                  </Tooltip>
                </>
              )}
            {!!leaveRequest.leavePDF &&
              leaveRequest.requestStatus === "APPROVED" && (
                <Tooltip title="Download request">
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      if (!leaveRequest.leavePDF) return;

                      const link = document.createElement("a");

                      const pdfDataUrl = leaveRequest.leavePDF.startsWith("data:")
                        ? leaveRequest.leavePDF
                        : `data:application/pdf;base64,${leaveRequest.leavePDF}`;

                      link.href = pdfDataUrl;
                      const safeEmployee = leaveRequest.employeeName || "employee";
                      link.download = `Cerere-concediu-ReqNo-${leaveRequest.id}-${safeEmployee}.pdf`;

                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  />
                </Tooltip>
              )}
          </div>
        ),
      },
      {
        title: "Request ID",
        dataIndex: "id",
        key: "id",
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) => a.id - b.id,
        sortDirections: SORT_DIRECTIONS,
        render: (_: unknown, record: LeaveRequest) => (
          <RequestIdLink id={record.id} />
        ),
      },
      {
        title: "Requested at",
        dataIndex: "requestCreationDate",
        key: "requestCreationDate",
        ellipsis: true,
        responsive: ["md"],
        render: (value: LeaveRequest["requestCreationDate"]) =>
          formatRequestedAt(value),
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.requestCreationDate || "").localeCompare(
            b.requestCreationDate || "",
          ),
        defaultSortOrder: "descend",
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "Name",
        dataIndex: "employeeName",
        key: "employeeName",
        ellipsis: true,
        ...getTextColumnFilterProps("employeeName", "Search employee"),
      },
      { ...activeColumn<LeaveRequest>(), responsive: ["md"] },
      {
        title: "Request Type",
        dataIndex: "requestType",
        key: "requestType",
        ellipsis: true,
        responsive: ["md"],
        render: (value: LeaveRequest["requestType"]) => formatEnumLabel(value),
        filters: requestTypeFilterOptions,
        filterMultiple: false,
        onFilter: (value, record) => record.requestType === value,
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        ellipsis: true,
        responsive: ["md"],
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.startDate || "").localeCompare(b.startDate || ""),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        ellipsis: true,
        responsive: ["md"],
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.endDate || "").localeCompare(b.endDate || ""),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "Requested days",
        key: "requestedDays",
        ellipsis: true,
        responsive: ["md"],
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.requestedDays || a.leaveDays?.length || 0) -
          (b.requestedDays || b.leaveDays?.length || 0),
        sortDirections: SORT_DIRECTIONS,
        render: (_: unknown, record: LeaveRequest) =>
          record.requestedDays || record.leaveDays?.length || "-",
      },
      {
        title: "Substitute",
        dataIndex: "substituteName",
        key: "substituteName",
        ellipsis: true,
        responsive: ["md"],
        ...getTextColumnFilterProps("substituteName", "Search substitute"),
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        ellipsis: true,
        responsive: ["lg"],
        ...getTextColumnFilterProps("comment", "Search comment"),
      },
      {
        title: "Status",
        dataIndex: "requestStatus",
        key: "requestStatus",
        ellipsis: true,
        responsive: ["md"],
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.requestStatus || "").localeCompare(b.requestStatus || ""),
        sortDirections: SORT_DIRECTIONS,
        filters: requestStatusFilterOptions,
        filterMultiple: false,
        onFilter: (value, record) => record.requestStatus === value,
        render: (value: LeaveRequest["requestStatus"]) => (
          <Tag className={getRequestStatusType(value)}>
            {formatEnumLabel(value)}
          </Tag>
        ),
      },
      {
        title: "Approver",
        dataIndex: "approverName",
        key: "approverName",
        ellipsis: true,
        responsive: ["md"],
        render: (value: LeaveRequest["approverName"]) => value || "-",
        ...getTextColumnFilterProps("approverName", "Search approver"),
      },
      {
        title: "Documents",
        key: "documents",
        responsive: ["md"],
        render: (_: unknown, record: LeaveRequest) => (
          <ViewDocumentsBtn documents={record.documents} />
        ),
      },
    ],
    [
      canReviewRequest,
      getTextColumnFilterProps,
      isMobile,
      requestStatusFilterOptions,
      requestTypeFilterOptions,
    ],
  );

  return (
    <div className="user-leave-requests w-full">
      <Typography.Title level={4} className="table-header">
        {canViewAllRequests ? "All" : "Team"} Leave requests
      </Typography.Title>

      <div className="table-actions mb-2 flex justify-start gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <EmployeeStatusFilter
            value={employeeStatusFilter}
            onChange={setEmployeeStatusFilter}
          />
          {canExportRequests && (
            <RangePicker
              allowClear
              allowEmpty={[true, true]}
              format="DD/MM/YYYY"
              value={requestDateRange}
              onChange={(range) => setRequestDateRange(range)}
              placeholder={["Start date", "End date"]}
            />
          )}
        </div>
        {canExportRequests && (
          <>
            <div className="flex gap-2">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleReportDownload}
                loading={isExporting}
              >
                Export filtered
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setIsMonthlyExportOpen(true)}
              >
                Export monthly
              </Button>
            </div>
          </>
        )}
      </div>

      <MonthlyExportModal
        isOpen={isMonthlyExportOpen}
        setIsOpen={setIsMonthlyExportOpen}
        employeeStatusFilter={employeeStatusFilter}
      />

      <RequestStatusModal
        isOpen={isRequestStatusOpen}
        setIsOpen={setIsRequestStatusOpen}
        request={selectedRequest ? selectedRequest : null}
        status={selectedRequestStatus}
        source={"default"}
        onCancelConfirm={fetchLeaveRequests}
      />

      <div className="table-container">
        <Table
          className="view-table"
          columns={tableColumns}
          dataSource={dateFilteredLeaveRequests}
          loading={isLoading}
          rowKey={(record) => `${record.id}-${record.requestType}`}
          scroll={{ x: isMobile ? 480 : true, y: fixedTableScrollY }}
          locale={{
            emptyText: leaveRequests.length === 0 ? (
              <Empty
                description={`No ${canViewAllRequests ? "leave requests" : "team leave requests"} found.`}
              />
            ) : null,
          }}
          pagination={
            leaveRequests.length > (canViewAllRequests ? pageSize : rowsPerPage)
              ? {
                  current: currentPage,
                  pageSize: canViewAllRequests ? pageSize : rowsPerPage,
                  showSizeChanger: canViewAllRequests,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  position: ["bottomCenter"],
                }
              : false
          }
          onChange={(pagination, _filters, _sorter, extra) => {
            setCurrentPage(pagination.current || 1);
            if (canViewAllRequests) {
              setPageSize(pagination.pageSize || rowsPerPage);
            }
            setCurrentViewData(extra.currentDataSource as LeaveRequest[]);
          }}
        />
      </div>
    </div>
  );
};

export default TeamLeaveRequests;
